import React, { InputHTMLAttributes, ReactNode, useState } from 'react';

/**
 * 🍎 Input Component - Apple-style modern input with floating label
 * 
 * Features:
 * - Floating label animation
 * - Icon support
 * - Error state
 * - Helper text
 * - Clean Apple-like design
 */

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  inputSize?: 'small' | 'medium' | 'large';
}

const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  inputSize = 'medium',
  className = '',
  id,
  value,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  // Fix: controlla meglio se il campo ha un valore
  const hasValue = value !== undefined && value !== null && String(value).length > 0;
  // I campi data/ora mostrano sempre il loro segnaposto (es. gg/mm/aaaa): l'etichetta resta in alto
  const tipoConSegnaposto = ['date', 'time', 'datetime-local', 'month', 'week'].includes(String(props.type));
  const isFloating = isFocused || hasValue || tipoConSegnaposto;

  // Generate unique ID if not provided
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  // Size classes - aumentato padding per label floating
  const sizeClasses = {
    small: label ? 'px-4 pt-5 pb-2 text-sm' : 'px-4 py-2 text-sm',
    medium: label ? 'px-4 pt-6 pb-3 text-base' : 'px-4 py-3 text-base',
    large: label ? 'px-4 pt-7 pb-4 text-lg' : 'px-4 py-4 text-lg',
  };

  // Icon padding
  const iconPaddingClasses = icon
    ? iconPosition === 'left'
      ? 'pl-12'
      : 'pr-12'
    : '';

  // Width class
  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <div className={`${widthClass} ${className}`}>
      <div className="relative">
        {/* Icon */}
        {icon && (
          <div
            className={`absolute top-1/2 transform -translate-y-1/2 ${
              iconPosition === 'left' ? 'left-4' : 'right-4'
            } text-gray-400 dark:text-gray-500 pointer-events-none`}
          >
            {icon}
          </div>
        )}

        {/* Input */}
        <input
          id={inputId}
          value={value}
          className={`
            input
            ${sizeClasses[inputSize]}
            ${iconPaddingClasses}
            ${error ? 'border-red-500 focus:ring-red-500' : ''}
            ${widthClass}
            peer
          `}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={label ? '' : props.placeholder}
          {...props}
        />

        {/* Floating Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={`
              absolute left-4 transition-all duration-200 pointer-events-none
              ${isFloating
                ? 'top-2 text-xs text-primary-600 dark:text-primary-400'
                : 'top-1/2 -translate-y-1/2 text-base text-gray-500 dark:text-gray-400'
              }
              ${iconPosition === 'left' && icon ? 'left-12' : ''}
            `}
          >
            {label}
          </label>
        )}
      </div>

      {/* Helper Text or Error */}
      {(error || helperText) && (
        <p
          className={`mt-2 text-sm ${
            error
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
};

export default Input;

